<template>
  <v-off-canvas
    :id="id"
    :backdrop-static="true"
    :offcanvas-class="offcanvasClass"
    @closed="$emit('closed')"
  >
    <resizable-container :position="offcanvasClass === 'offcanvas-start' ? 'left' : 'right'">
      <off-canvas-header>
        <h5 class="offcanvas-title">{{ $t("tourPricing.invoice.createNew") }}</h5>
      </off-canvas-header>

      <off-canvas-body>
        <form id="tour-invoice-create-form" class="d-flex flex-column justify-content-between h-100">
          <div>
            <v-checkbox
              v-if="preSelectedInvoice === '' || preSelectedInvoice === 'free-text'"
              id="manual-invoice-checkbox"
              v-model="isManualInvoice"
              :label="`${$t('tourPricing.invoice.manualInvoice')}`"
            />
            <v-checkbox
              v-if="preSelectedInvoice === ''"
              id="invoice-in-total"
              v-model="inTotal"
              :label="`${$t('tourPricing.invoice.invoiceInTotal')}`"
            />
            <template v-if="!isManualInvoice">
              <v-form
                id="supplier-invoice-type-select"
                :label="`${$t('general.shared.inVoiceTypes')}*`"
                :yup-errors-variable="errors.inVoiceType"
                form-type="outline"
              >
                <v-select
                  v-model="inVoiceType"
                  :class="{ 'is-invalid': !!errors.inVoiceType }"
                  :options="conditionalInvoiceTypes"
                  label="label"
                  :reduce="(returnValue) => returnValue"
                >
                  <template v-slot:no-options>
                    <div class="my-4">{{ $t("general.vueSelect.noOptions") }}</div>
                  </template>
                </v-select>
              </v-form>
              <template v-if="!inTotal">
                <v-form-input
                  id="invoice-client-email"
                  v-model="clientEmail"
                  :label="`${$t('tourPricing.invoice.clientEmail')}*`"
                  :yup-errors-variable="errors.clientEmail"
                  autocomplete="off"
                  form-type="outline"
                />
                <v-form-input
                  id="invoice-client-name"
                  v-model="clientName"
                  :label="`${$t('tourPricing.invoice.clientName')}*`"
                  :yup-errors-variable="errors.clientName"
                  autocomplete="off"
                  form-type="outline"
                />
              </template>
              <v-form
                id="supplier-invoice-type-payment-select"
                :label="`${$t('general.shared.inVoiceRecived')}*`"
                :yup-errors-variable="errors.inVoiceType"
                form-type="outline"
              >
                <v-select
                  v-model="inVoiceRecivedVia"
                  :options="inVoiceRecived"
                  label="label"
                  :clearable="false"
                  :reduce="(returnValue) => returnValue.key"
                >
                  <template v-slot:no-options>
                    <div class="my-4">{{ $t("general.vueSelect.noOptions") }}</div>
                  </template>
                </v-select>
              </v-form>
              <template v-if="inVoiceRecivedVia === 'credit_card'">
                <v-form-input
                  id="tour-credit-card-extracharge"
                  v-model="extracharge"
                  :label="$t('general.shared.extracharge')"
                  autocomplete="off"
                  form-type="outline"
                  type="number"
                />
              </template>
              <template v-if="inVoiceType?.key !== 'flights'">
                <v-form-input
                  id="tour-total-invoice"
                  v-model="userDefinedInvoice"
                  v-if="inVoiceType?.key === 'free-text' || inVoiceType?.key === 'services-info'"
                  :label="$t('general.shared.totalTrip')"
                  autocomplete="off"
                  form-type="outline"
                />
                <v-form-input
                  v-else
                  id="tour-total-invoice"
                  :model-value="inVoiceType === null ? '' : quotePrice"
                  disabled
                  :label="$t('general.shared.totalTrip')"
                  autocomplete="off"
                  form-type="outline"
                />
                <template
                  v-if="
                    inVoiceType?.key !== 'free-text' &&
                    inVoiceType?.key !== 'services-info' &&
                    inVoiceType?.key !== '100'
                  "
                >
                  <v-form-input
                    id="tour-total-invoice"
                    :model-value="
                      inVoiceType === null
                        ? ''
                        : inVoiceType?.key === 'remaining'
                        ? invoiceAmount?.tour?.total_remaining
                        : totalInvoiceAmount
                    "
                    disabled
                    :label="$t('general.shared.totalInvoice')"
                    autocomplete="off"
                    form-type="outline"
                  />
                  <v-form-input
                    id="tour-total-invoice"
                    :model-value="inVoiceType === null ? '' : invoiceAmount?.tour?.total_remaining"
                    disabled
                    :label="$t('general.shared.remaining')"
                    autocomplete="off"
                    form-type="outline"
                  />
                </template>
              </template>

              <div class="form-outline" v-if="inVoiceType?.key !== 'flights'">
                <label class="label">
                  <span>{{ $t("general.shared.concept") }}</span>
                </label>
                <v-html-editor
                  id="tour-invoice-concept"
                  ref="tour-invoice-concept-ref"
                  :height="'200px'"
                  v-model="conceptsData"
                  @input="onConceptsDataChange"
                />
              </div>
              <template v-if="inVoiceType?.key === 'flights' || inVoiceType?.key === '30-flights'">
                <div class="mb-4 mt-4">{{ $t("general.shared.selectFlight") }}</div>
                <template v-for="flight in flightsInformation" :key="flight.id">
                  <div>
                    <div v-for="(item, idx) in flight.itemResourcesGrouped" :key="idx">
                      <v-checkbox
                        :id="`tour-flight-${item.id}`"
                        :value="item.id"
                        :model-value="selectedItemIds.includes(item.id)"
                        :label="`${flight.name} | ${item.name}`"
                        @change="toggleSelection(item.id)"
                      />
                    </div>
                  </div>
                </template>
                <template v-for="(flight, index) in selectedItems" :key="flight.id">
                  <div class="form-outline">
                    <label class="label">
                      <span>{{ $t("general.shared.flights") }}#{{ index + 1 }}</span>
                    </label>
                    <v-html-editor
                      :id="`tour-flights-${flight.id}`"
                      :ref="`tour-flights-ref-${flight.id}`"
                      :height="'200px'"
                      :value="flightConceptsData[flight.id]"
                      @input="(newData) => onFlightConceptsDataChange(flight.id, newData)"
                    />
                  </div>
                  <v-form-input
                    :id="`tour-flight-ticket-${flight.id}`"
                    v-model="flight.quantity"
                    disabled
                    :label="$t('general.shared.tickets')"
                    autocomplete="off"
                    form-type="outline"
                  />
                  <v-form-input
                    :id="`tour-flight-price-per-unit-${flight.id}`"
                    :model-value="flight.netPrice / flight.quantity"
                    disabled
                    :label="$t('general.shared.ppu')"
                    autocomplete="off"
                    form-type="outline"
                  />
                  <v-form-input
                    :id="`tour-total-flight-${flight.id}`"
                    :model-value="Number(flight.netPrice) + Number(flight.serviceFee) || flight.netPrice"
                    disabled
                    :label="$t('general.shared.totalFlight')"
                    autocomplete="off"
                    form-type="outline"
                  />
                  <v-form-input
                    :id="`tour-flight-service-fee-${flight.id}`"
                    v-model="flight.serviceFee"
                    :label="$t('general.shared.serviceFee')"
                    autocomplete="off"
                    form-type="outline"
                  />
                </template>
              </template>

              <v-form
                id="supplier-invoice-to-select"
                label="Invoice To"
                :yup-errors-variable="errors.inVoiceTo"
                form-type="outline"
                v-if="version !== ''"
              >
                <v-select
                  v-model="inVoiceTo"
                  :class="{ 'is-invalid': !!errors.inVoiceTo }"
                  :options="paxList"
                  label="label"
                  :reduce="(o: { value: any; }) => o.value"
                >
                  <template v-slot:no-options>
                    <div class="my-4">{{ $t("general.vueSelect.noOptions") }}</div>
                  </template>
                </v-select>
              </v-form>

              <div class="form-outline">
                <label class="label">
                  <span>Client Data</span>
                </label>
                <div class="form-control">
                  <div class="form-outline">
                    <div class="w-100 pt-8 pb-8 d-flex justify-content-start">
                      <v-button
                        class="btn-icon p-1 rounded-circle"
                        size="sm"
                        variant="gradient"
                        @click="toggleCheckboxes"
                      >
                        <template v-if="showCheckboxes">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            xmlns:xlink="http://www.w3.org/1999/xlink"
                            version="1.1"
                            width="10"
                            height="10"
                            viewBox="0 0 256 256"
                            xml:space="preserve"
                          >
                            <defs></defs>
                            <g
                              style="
                                stroke: none;
                                stroke-width: 0;
                                stroke-dasharray: none;
                                stroke-linecap: butt;
                                stroke-linejoin: miter;
                                stroke-miterlimit: 10;
                                fill: none;
                                fill-rule: nonzero;
                                opacity: 1;
                              "
                              transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)"
                            >
                              <path
                                d="M 86.5 48.5 h -83 C 1.567 48.5 0 46.933 0 45 s 1.567 -3.5 3.5 -3.5 h 83 c 1.933 0 3.5 1.567 3.5 3.5 S 88.433 48.5 86.5 48.5 z"
                                style="
                                  stroke: none;
                                  stroke-width: 1;
                                  stroke-dasharray: none;
                                  stroke-linecap: butt;
                                  stroke-linejoin: miter;
                                  stroke-miterlimit: 10;
                                  fill: rgb(29, 29, 27);
                                  fill-rule: nonzero;
                                  opacity: 1;
                                "
                                transform=" matrix(1 0 0 1 0 0) "
                                stroke-linecap="round"
                              />
                              <path
                                d="M 86.5 48.5 h -83 C 1.567 48.5 0 46.933 0 45 s 1.567 -3.5 3.5 -3.5 h 83 c 1.933 0 3.5 1.567 3.5 3.5 S 88.433 48.5 86.5 48.5 z"
                                style="
                                  stroke: none;
                                  stroke-width: 1;
                                  stroke-dasharray: none;
                                  stroke-linecap: butt;
                                  stroke-linejoin: miter;
                                  stroke-miterlimit: 10;
                                  fill: rgb(29, 29, 27);
                                  fill-rule: nonzero;
                                  opacity: 1;
                                "
                                transform=" matrix(1 0 0 1 0 0) "
                                stroke-linecap="round"
                              />
                            </g>
                          </svg>
                        </template>
                        <template v-else>
                          <v-icon icon="plus" />
                        </template>
                      </v-button>
                    </div>
                    <template v-if="showCheckboxes">
                      <fieldset>
                        <!-- <legend class="visually-hidden">{{ $t('tourPricing.invoice.amount') }}</legend> -->
                        <div class="row gx-12">
                          <!-- <div class="col-12">
                <div class="form-outline">
                  <label class="label">
                    <span>{{ $t('tourPricing.invoice.amount') }}*</span>
                  </label>

                  <v-form-input
                    id="tour-invoice-create-amount"
                    v-model="amount"
                    :yup-errors-variable="errors.amount"
                    autocomplete="off"
                    form-type="input-group"
                    onwheel="return false;"
                    right-addon="€"
                    step=".01"
                    type="number"
                  />
                </div>
              </div> -->
                          <v-form-input
                            id="tour-invoice-create-id-identity"
                            v-model="idIdentity"
                            :label="$t('tourPricing.invoice.idIdentity')"
                            :yup-errors-variable="errors.idIdentity"
                            autocomplete="off"
                            class="col-12"
                            form-type="outline"
                          />

                          <v-form-input
                            id="tour-invoice-create-id-invoice-name"
                            v-model="invoiceName"
                            :label="$t('tourPricing.invoice.invoiceName')"
                            autocomplete="off"
                            class="col-12"
                            form-type="outline"
                          />

                          <v-form-input
                            id="tour-invoice-create-vat"
                            v-model="vat"
                            :label="$t('tourPricing.invoice.vat')"
                            :yup-errors-variable="errors.vat"
                            autocomplete="off"
                            class="col-12"
                            form-type="outline"
                          />
                        </div>
                      </fieldset>
                      <div class="search-separator-container pb-32">
                        <div class="separator-text-container">
                          <span class="separator-text text-sm">{{ $t("tourPricing.invoice.address") }}</span>
                        </div>
                      </div>

                      <fieldset>
                        <legend class="visually-hidden">{{ $t("general.address.address") }}</legend>

                        <div class="row gx-12">
                          <v-input-address
                            id="tour-invoice-create-address-search"
                            ref="tour-invoice-create-address-search-ref"
                            :form-floating="false"
                            :get-address="true"
                            :label="$t('general.address.streetAddress')"
                            :value="address"
                            class="col-12"
                            @address="setAddress"
                          />

                          <v-form-input
                            id="tour-invoice-create-second-address"
                            v-model="addressTwo"
                            :label="$t('general.address.aptSuit')"
                            :yup-errors-variable="errors.addressTwo"
                            autocomplete="off"
                            class="col-12"
                            form-type="outline"
                          />

                          <v-form-input
                            id="tour-invoice-create-country"
                            v-model="countryLabel"
                            :label="$t('general.address.country')"
                            :yup-errors-variable="errors.countryLabel"
                            autocomplete="off"
                            class="col-12"
                            form-type="outline"
                          />

                          <v-form-input
                            id="tour-invoice-create-postal-code"
                            v-model="postalCode"
                            :label="$t('general.address.postalCode')"
                            :yup-errors-variable="errors.postalCode"
                            autocomplete="off"
                            class="col-12"
                            form-type="outline"
                          />

                          <v-form-input
                            id="tour-invoice-create-city"
                            v-model="city"
                            :label="$t('general.address.city')"
                            :yup-errors-variable="errors.city"
                            autocomplete="off"
                            class="col-12"
                            form-type="outline"
                          />

                          <v-form-input
                            id="tour-invoice-create-region"
                            v-model="region"
                            :label="$t('general.address.provinceStateRegion')"
                            :yup-errors-variable="errors.region"
                            autocomplete="off"
                            class="col-12"
                            form-type="outline"
                          />
                        </div>
                      </fieldset>
                    </template>
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              <fieldset>
                <v-form-input
                  id="invoice-number"
                  v-model="invoiceNumber"
                  :label="$t('tourPricing.invoice.invoiceNumber')"
                  autocomplete="off"
                  form-type="outline"
                  :yup-errors-variable="errors.invoiceNumber"
                />
                <v-form-input
                  id="invoice-description"
                  v-model="invoiceDescription"
                  :label="$t('tourPricing.invoice.invoiceDescription')"
                  autocomplete="off"
                  form-type="outline"
                  :yup-errors-variable="errors.invoiceDescription"
                />
                <v-form-input
                  id="case-invoice-amount"
                  v-model="amount"
                  :label="$t('general.shared.amount')"
                  autocomplete="off"
                  :yup-errors-variable="errors.amount"
                  form-type="outline"
                />
                <v-form-input
                  id="client-email"
                  v-model="clientEmail"
                  :label="$t('general.shared.clientEmail')"
                  autocomplete="off"
                  :yup-errors-variable="errors.clientEmail"
                  form-type="outline"
                  type="email"
                />
                <v-form
                  id="supplier-invoice-to-select"
                  label="Invoice To"
                  :yup-errors-variable="errors.inVoiceTo"
                  form-type="outline"
                >
                  <v-select
                    v-model="inVoiceTo"
                    :class="{ 'is-invalid': !!errors.inVoiceTo }"
                    :options="paxList"
                    label="label"
                    :reduce="(o: { value: any; }) => o.value"
                  >
                    <template v-slot:no-options>
                      <div class="my-4">{{ $t("general.vueSelect.noOptions") }}</div>
                    </template>
                  </v-select>
                </v-form>
                <div id="case-init-date" class="form-outline">
                  <label class="label">
                    <span>{{ $t("general.shared.initDate") }}*</span>
                  </label>
                  <Datepicker
                    v-model="invoiceDate"
                    :enable-time-picker="false"
                    :input-class-name="`form-control ${!!errors.invoiceDate ? 'is-invalid' : ''}`"
                    :month-change-on-scroll="false"
                    auto-apply
                    calendar-cell-class-name="init-date-cell"
                    class="form-datepicker"
                  />
                  <span v-if="!!errors.invoiceDate" class="invalid-feedback d-block">
                    {{ errors.invoiceDate }}
                  </span>
                </div>
              </fieldset>
            </template>
          </div>

          <v-button
            :is-loading="accepted"
            :disabled="!isFormValid || accepted"
            class="btn-submit-form btn-icon w-fit align-self-end mt-16"
            size="sm"
            variant="primary"
            @click="createInvoice"
          >
            {{ $t("general.button.create") }}
            <v-icon icon="arrow-right" size="sm" space="ms-12" />
          </v-button>
        </form>
      </off-canvas-body>
    </resizable-container>
  </v-off-canvas>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import * as yup from "yup";
import { useField } from "vee-validate";
import vSelect from "vue-select";
import { VFormInput, VForm, VCheckbox } from "@uniqoders/form";
import VHtmlEditor from "@/components/vendor/htmlEditor/VHtmlEditor.vue";
import { useI18n } from "vue-i18n";
import VOffCanvas from "@/components/vendor/basic/offcanvas/VOffCanvas.vue";
import OffCanvasHeader from "@/components/vendor/basic/offcanvas/OffCanvasHeader.vue";
import OffCanvasBody from "@/components/vendor/basic/offcanvas/OffCanvasBody.vue";
import useFormValidation from "@/helpers/form";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import useConcepts from "@/helpers/Concepts";
import { findConceptByKey } from "@/helpers/ConceptHelper";
import VInputAddress from "@/components/vendor/basic/form/VInputAddress.vue";
import { IItemsTypes } from "@/views/case/parts/TheTourPricing.vue";
import api from "@/api";
import ResizableContainer from "@/components/resizablecontainer/ResizableContainer.vue";
import { mapConcepts } from "@/helpers/ConceptHelper";
import { mapState } from "pinia";
import { useUserStore } from "@/stores/user";
import { DateTime } from "luxon";
import Datepicker from "@vuepic/vue-datepicker";

export default defineComponent({
  name: "ThePricingInvoiceCreateOffCanvas",
  components: {
    vSelect,
    VInputAddress,
    VIcon,
    VButton,
    VHtmlEditor,
    VForm,
    VFormInput,
    VCheckbox,
    OffCanvasBody,
    OffCanvasHeader,
    VOffCanvas,
    ResizableContainer,
    Datepicker,
  },
  emits: ["closed", "reloadResources", "invoiceList"],
  props: {
    agency: {
      type: String,
      required: true,
    },
    caseId: {
      type: Number,
      required: true,
    },
    tourId: {
      type: Number,
      required: true,
    },
    items: {
      type: Object as PropType<IItemsTypes>,
      required: true,
    },
    quotePrice: {
      type: Number || String,
      required: true,
    },
    pricing: {
      type: Object,
      required: true,
    },
    totalInvoice: {
      type: Number,
      required: true,
    },
    invoiceAmount: {
      type: Object,
      required: false,
      default: () => null,
    },
    version: {
      type: String,
      required: true,
      default: "",
    },
    offcanvasClass: {
      type: String,
      required: false,
      default: "offcanvas-end",
    },
    id: {
      type: String,
      required: false,
      default: "pricing-invoice-create-off-canvas",
    },
  },
  setup(props) {
    const { t } = useI18n();

    const rules = yup.object({
      type: yup.string().required().nullable().label(t("general.shared.type")),
      inVoiceType: yup
        .object({
          key: yup.string().required(),
          label: yup.string().required(),
        })
        .required()
        .nullable()
        .label(t("general.shared.inVoiceTypes")),
      currency: yup.string().nullable().label(t("tourPricing.invoice.currency")),
      amount: yup
        .number()
        .required()
        .nullable()
        .min(0)
        // eslint-disable-next-line no-restricted-globals
        .transform((value) => (isNaN(value) ? undefined : value))
        .label(t("tourPricing.invoice.number")),
      invoiceStatus: yup.string().nullable().label(t("general.shared.invoiceStatus")),
      paymentStatus: yup.string().nullable().label(t("general.shared.paymentStatus")),
      address: yup.string().nullable().label(t("general.address.streetAddress")),
      addressTwo: yup.string().nullable().label(t("general.address.aptSuit")),
      city: yup.string().nullable().label(t("general.address.city")),
      countryLabel: yup.string().nullable().label(t("general.address.country")),
      region: yup.string().nullable().label(t("general.address.provinceStateRegion")),
      postalCode: yup.string().nullable().label(t("general.address.postalCode")),
      idIdentity: yup.string().nullable().label(t("tourPricing.invoice.idIdentity")),
      vat: yup.string().nullable().label(t("tourPricing.invoice.vat")),
      clientEmail: yup.string().email().required().label(t("tourPricing.invoice.clientEmail")),
      clientName: yup.string().required().nullable().label(t("tourPricing.invoice.clientName")),
      invoiceNumber: yup.string().required().nullable().label(t("tourPricing.invoice.invoiceNumber")),
      invoiceDescription: yup.string().required().nullable().label(t("tourPricing.invoice.invoiceDescription")),
      invoiceDate: yup.string().required().nullable().label(t("tourPricing.invoice.initDate")),
      inVoiceTo: yup
        .mixed()
        .nullable()
        .when("$version", {
          is: (version: string) => version !== "",
          then: (schema) => schema.required("Invoice To is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
    });

    const initialValues = {
      amount: props.totalInvoice,
      quotPrice: props.quotePrice,
    };

    const form = useFormValidation(rules, initialValues);

    const { value: currency } = useField("currency");
    const { value: amount } = useField("amount");
    const { value: invoiceStatus } = useField("invoiceStatus");
    const { value: paymentStatus } = useField("paymentStatus");

    const { value: address } = useField("address");
    const { value: addressTwo } = useField("addressTwo");
    const { value: city } = useField("city");
    const { value: countryLabel } = useField("countryLabel");
    const { value: region } = useField("region");
    const { value: postalCode } = useField("postalCode");
    const { value: idIdentity } = useField("idIdentity");
    const { value: vat } = useField("vat");
    const { value: inVoiceType } = useField("inVoiceType");
    const { value: clientEmail } = useField("clientEmail");
    const { value: clientName } = useField("clientName");
    const { value: invoiceNumber } = useField("invoiceNumber");
    const { value: invoiceDescription } = useField("invoiceDescription");
    const { value: invoiceDate } = useField("invoiceDate");
    const { value: inVoiceTo } = useField("inVoiceTo");

    return {
      ...useConcepts(),
      ...form,
      findConceptByKey,
      currency,
      amount,
      invoiceStatus,
      paymentStatus,
      address,
      addressTwo,
      city,
      countryLabel,
      region,
      postalCode,
      idIdentity,
      vat,
      inVoiceType,
      clientEmail,
      clientName,
      invoiceNumber,
      invoiceDescription,
      invoiceDate,
      inVoiceTo,
    };
  },
  data() {
    return {
      resourcesArray: [] as number[],
      showCheckboxes: false as boolean,
      flightConceptsData: {} as any,
      conceptsData: this.generateConceptsData(),
      flightsInformation: [],
      inVoiceRecivedVia: "wise",
      inVoiceRecived: [
        { key: "wise", label: "Wise" },
        { key: "credit_card", label: this.$t("general.shared.card") },
      ],
      extracharge: null as unknown as number,
      invoiceName: null as unknown as string,
      selectedItemIds: [] as number[],
      userDefinedInvoice: this.quotePrice,
      inTotal: true as boolean,
      isManualInvoice: false as boolean,
      paxList: [] as any,
    };
  },
  methods: {
    toggleCheckboxes() {
      this.showCheckboxes = !this.showCheckboxes;
    },
    async setAddress(address: any) {
      this.address = address.name;
      this.countryLabel = address.country.longName;
      this.city = address.locality.longName;
      this.region = address.regionProvince.longName;
      this.postalCode = address.postalCode.longName;
    },
    generateConceptsData(): string {
      const selectLabelKey = this.inVoiceTypes?.find((type) => type.key === this.inVoiceType?.key)?.label || null;

      const data = `${selectLabelKey} [${this.currentTour?.name} | ${
        this.currentTour?.travelType ?? this.currentTour?.travel_type
      } | ${this.currentTour?.initDate ?? this.currentTour?.start_date} | ${
        this.currentTour?.endDate ?? this.currentTour?.end_date
      }] ${this.$t("general.invoice.remaining_amount")} [${this.invoiceAmount?.tour?.total_remaining}] ${this.$t(
        "general.invoice.due_amount"
      )} ]`;

      const remainingData = `${this.$t("general.invoice.remaining_amount_trip")} [${this.currentTour?.name} | ${
        this.currentTour?.travelType ?? this.currentTour?.travel_type
      } | ${this.currentTour?.initDate ?? this.currentTour?.start_date} | ${
        this.currentTour?.endDate ?? this.currentTour?.end_date
      }]`;

      const totalTripData = `${this.$t("general.shared.totalTrip")} [${this.currentTour?.name} | ${
        this.currentTour?.travelType ?? this.currentTour?.travel_type
      } | ${this.currentTour?.initDate ?? this.currentTour?.start_date} | ${
        this.currentTour?.endDate ?? this.currentTour?.end_date
      }]`;

      const finalData =
        this.inVoiceType !== null
          ? selectLabelKey === "Remaining"
            ? remainingData
            : selectLabelKey === "100%"
            ? totalTripData
            : selectLabelKey === "Services info" || selectLabelKey === "Free text"
            ? ""
            : data
          : "";

      return finalData;
    },
    updateConceptsData() {
      this.conceptsData = this.generateConceptsData();
    },
    onConceptsDataChange(newData: string) {
      this.conceptsData = newData;
    },
    generateFlightConceptsData(flight: any) {
      const data =
        this.inVoiceType?.key === "flights" || this.inVoiceType?.key === "30-flights"
          ? [
              "Airflight",
              flight.resources[0]?.name,
              flight.resources[0]?.nReserve,
              flight.resources[0]?.flightNumber,
              flight.resources[0]?.date ? DateTime.fromISO(flight.resources[0].date).toFormat("d-M-yyyy") : null,
              flight.resources[0]?.initHour,
              flight.resources[0]?.endDate ? DateTime.fromISO(flight.resources[0].endDate).toFormat("d-M-yyyy") : null,
              flight.resources[0]?.endHour,
            ]
              .filter(Boolean) // Removes falsy values like null, undefined, or empty strings
              .join(" | ")
          : "";
      return data;
    },
    updateFlightConceptsData() {
      this.selectedItems.forEach((flight: any) => {
        this.flightConceptsData[flight.id] = this.generateFlightConceptsData(flight);
      });
    },
    onFlightConceptsDataChange(flightId: any, newData: string) {
      this.flightConceptsData[flightId] = newData;
    },
    handleConceptsUpdates() {
      this.updateConceptsData();
      this.updateFlightConceptsData();
    },
    async createInvoice() {
      try {
        this.toggleAccepted();
        const flightResources = this.selectedItems.map((flight: any, index: number) => ({
          id: flight.id,
          name: `Flights ${index + 1}`,
          amount: Number(flight.netPrice) + Number(flight.serviceFee),
          pax: flight.quantity,
          pricePerUnit: flight.netPrice / flight.quantity,
          serviceFee: flight.serviceFee,
        }));
        const formattedFlights = flightResources.map((flight: any) => ({
          ...flight,
          flight_concept: this.flightConceptsData[flight.id],
        }));
        const data = {
          invoice_type: this.inVoiceType.key,
          name: this.inVoiceType.label,
          total_trip: this.quotePrice,
          total_invoice: this.shouldIncludeTotalInvoice ? this.totalInvoiceAmount : 0,
          remaining: this.shouldIncludeRemaining ? this.invoiceAmount?.tour?.total_remaining : 0,
          concepts: this.conceptsData,
          currency: "EUR",
          line1: this.address,
          line2: this.addressTwo,
          postal_code: this.postalCode,
          country: this.countryLabel,
          city: this.city,
          state: this.region,
          id_identity: this.idIdentity,
          invoice_name: this.invoiceName,
          vat: this.vat,
          type: "emitted",
          flights: this.shouldIncludeFlights ? formattedFlights : [],
          receive_via: this.inVoiceRecivedVia,
          extra_charge: this.extracharge,
        };

        if (this.version !== "") {
          const isClient = typeof this.inVoiceTo === 'string' && this.inVoiceTo.startsWith('client_');
          if (isClient) {
            data.bill_to_client_id = parseInt(this.inVoiceTo.replace('client_', ''), 10);
          } else {
            data.pax_id = this.inVoiceTo;
          }
        }

        if (!this.inTotal) {
          data.client_email = this.clientEmail;
          data.client_name = this.clientName;
        }

        if (this.isManualInvoice) {
          const payload = {
            number: this.invoiceNumber,
            description: this.invoiceDescription,
            amount: this.amount,
            client_email: this.clientEmail,
            date: this.invoiceDate,
          };

          if (this.version !== "") {
            const isClient = typeof this.inVoiceTo === 'string' && this.inVoiceTo.startsWith('client_');
            if (isClient) {
              payload.bill_to_client_id = parseInt(this.inVoiceTo.replace('client_', ''), 10);
            } else {
              payload.pax_id = this.inVoiceTo;
            }
          }

          await api.case.caseManualInvoice(this.agency, this.caseId, payload);
        } else {
          if (this.version === "") {
            await api.invoice.createInvoice(this.agency, this.caseId, this.tourId, data);
          } else {
            await api.case.caseInvoices(this.agency, this.caseId, data);
          }
        }

        this.$toast.success(this.$t("general.shared.savedChanges"));

        this.$emit("closed");
        this.$emit("invoiceList");

        this.toggleAccepted();
      } catch (e: any) {
        this.toggleAccepted();

        console.error(e);
        this.$toast.error(e.response.data.message);
      }
    },
    flightsData(): any {
      const suppliers = this.pricing?.items?.filter((item: any) => item.name === "plane")[0]?.suppliers || [];

      this.flightsInformation = suppliers.map((flight: any) => ({
        ...flight,
        serviceFee: 0,
      }));
    },
    toggleSelection(id: number) {
      if (this.selectedItemIds.includes(id)) {
        this.selectedItemIds = this.selectedItemIds.filter((itemId) => itemId !== id);
      } else {
        this.selectedItemIds.push(id);
      }
    },
    async fetchPaxList() {
      try {
        const response = await api.case.casePaxList(this.agency, this.caseId);

        if (response) {
          const fullList = response?.content?.map((item: { pax: { name: any; id: any }; id: any }) => ({
            label: item.pax.name,
            value: item?.pax?.id,
            traveller_id: item?.pax?.id,
          })) ?? [];

          const clientRecipients = response?.meta?.recipients?.map((r: any) => ({
            label: r.label,
            value: r.value,
            client_id: r.client_id,
          })) ?? [];

          if (this.preSelectedPax?.length) {
            const preSelectedPax = fullList.filter((pax: any) => this.preSelectedPax.includes(pax.traveller_id));

            this.paxList = preSelectedPax?.map((p: any) => ({
              label: p.label,
              value: p?.traveller_id,
              traveller_id: p?.traveller_id,
            }));

            this.inVoiceTo = this.paxList[0]?.value ?? null;
          } else {
            this.paxList = [...clientRecipients, ...fullList];
            this.inVoiceTo = null;
          }
        }
      } catch (e: any) {
        console.error(e);
        this.$toast.error(e.response.data.message);
      }
    },
  },
  watch: {
    quotPrice: "handleConceptsUpdates",
    inVoiceType: "handleConceptsUpdates",
    selectedItems: {
      handler() {
        this.updateFlightConceptsData();
      },
      deep: true,
    },
    inTotal: {
      handler() {
        this.inVoiceType = this.inTotal ? null : { key: "free-text", label: this.$t("general.invoice.types.free-text") };
        this.isManualInvoice = !this.inTotal;
      },
      deep: true,
    },
    isManualInvoice(val) {
      if (val) this.inTotal = false;
    },
  },
  computed: {
    ...mapState(useUserStore, ["currentTour"]),
    supplierResourceTypes(): any {
      if (!!this.concepts && this.concepts["supplier.type"]) {
        return mapConcepts(this.concepts["supplier.type"]);
      }

      return [];
    },
    remaining(): any {
      return this.invoiceAmount?.tour?.total_remaining;
    },
    totalInvoiceAmount() {
      if (!this.inVoiceType) return 0;

      switch (this.inVoiceType?.key) {
        case "remaining":
          return this.invoiceAmount?.tour?.total_remaining;
        case "100":
          return this.quotePrice;
        case "free-text":
        case "services-info":
          return this.userDefinedInvoice || 0;
        default:
          return (this.quotePrice * 30) / 100;
      }
    },
    shouldIncludeTotalInvoice() {
      return !["flights"].includes(this.inVoiceType?.key);
    },
    shouldIncludeRemaining() {
      return !["free-text", "services-info", "100"].includes(this.inVoiceType?.key);
    },
    shouldIncludeFlights() {
      return ["flights", "30-flights"].includes(this.inVoiceType?.key);
    },
    selectedItems(): any {
      const data = this.flightsInformation.flatMap((flight) =>
        flight.itemResourcesGrouped.filter((item: any) => this.selectedItemIds.includes(item.id))
      );
      return data;
    },
    inVoiceTypes() {
      return [
        { key: "30", label: this.$t("general.invoice.types.30") },
        { key: "30-flights", label: this.$t("general.invoice.types.30-flights") },
        { key: "flights", label: this.$t("general.invoice.types.flights") },
        { key: "remaining", label: this.$t("general.invoice.types.remaining") },
        { key: "70", label: this.$t("general.invoice.types.70") },
        { key: "100", label: this.$t("general.invoice.types.100") },
        { key: "services-info", label: this.$t("general.invoice.types.services-info") },
        { key: "free-text", label: this.$t("general.invoice.types.free-text") },
      ];
    },
    conditionalInvoiceTypes() {
      if (this.inTotal) {
        return this.inVoiceTypes;
      } else {
        return this.inVoiceTypes.filter((type) => type.key === "free-text");
      }
    },
    isFormValid() {
      if (this.isManualInvoice) {
        return (
          this.invoiceNumber?.trim() &&
          this.invoiceDescription?.trim() &&
          this.amount &&
          this.clientEmail?.trim() &&
          this.invoiceDate
        );
      }

      const requiredFields = [];

      requiredFields.push(this.inVoiceType);
      requiredFields.push(this.inVoiceRecivedVia);

      if (!this.inTotal) {
        requiredFields.push(this.clientEmail);
        requiredFields.push(this.clientName);
      }

      // client extra form (address section)
      if (this.showCheckboxes) {
        requiredFields.push(this.idIdentity);
        requiredFields.push(this.invoiceName);
        requiredFields.push(this.vat);
        requiredFields.push(this.address);
        // addressTwo is OPTIONAL → do NOT validate
        requiredFields.push(this.countryLabel);
        requiredFields.push(this.postalCode);
        requiredFields.push(this.city);
        requiredFields.push(this.region);
      }

      // Validate all required fields
      return requiredFields.every((field) => {
        if (typeof field === "string") {
          return field.trim().length > 0;
        }
        return field !== null && field !== undefined;
      });
    },
  },
  mounted() {
    const preSelectedInvoiceType = this.inVoiceTypes?.find((type) => type.key === this.preSelectedInvoice);
    this.inVoiceType = this.preSelectedInvoice !== "" ? preSelectedInvoiceType : null;
    this.flightsData();
    this.fetchPaxList();
  },
});
</script>

<style lang="scss" scoped>
:deep(.input-group) {
  .form-control {
    padding-left: 1.5rem !important;
    padding-right: 1.5rem !important;
  }
}

.resource-type {
  border: 1px solid var(--uq-gray-400);
  border-radius: 0.25rem;
  padding: 0.5rem;
  margin-bottom: 1rem;

  .resource-supplier {
    margin-left: 1rem;
    position: relative;

    &:not(:last-child) {
      margin-bottom: 0.5rem;
      padding-bottom: 0.5rem;
    }

    &:not(:last-child):after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      height: 1px;
      width: 100%;
      background: var(--uq-gray-300);
    }
  }
}
</style>
